import { useEffect, useState } from 'react';
import {
  applyLoan,
  listLoans,
  reviewLoan,
  type EmployeeLoan,
} from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { StatusBadge } from '../components/StatusBadge';
import { Icon } from '../components/Icon';

export function Loans() {

  const { session, can } = useAuth();
  const [loans, setLoans] = useState<EmployeeLoan[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'active' | 'closed' | 'rejected'>('all');
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Apply Modal
  const [applyModal, setApplyModal] = useState(false);
  const [loanType, setLoanType] = useState<'advance' | 'personal_loan'>('advance');
  const [principal, setPrincipal] = useState(25000);
  const [tenure, setTenure] = useState(3);

  async function loadData() {
    try {
      setLoading(true);
      const res = await listLoans({
        viewAll: can('loans.manage') || can('organization.manage'),
      });
      setLoans(res.data || []);
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to load loans data' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData();
  }, []);

  const estInterestRate = loanType === 'advance' ? 0 : 8.5;
  const totalRepayable = loanType === 'advance' ? principal : principal + (principal * estInterestRate * (tenure / 12)) / 100;
  const monthlyEmi = tenure > 0 ? Math.round(totalRepayable / tenure) : principal;

  async function handleApply(e: React.FormEvent) {
    e.preventDefault();
    try {
      await applyLoan({
        loanType,
        principalAmount: Number(principal),
        tenureMonths: Number(tenure),
      });
      setMsg({ type: 'success', text: 'Loan/Advance application submitted successfully!' });
      setApplyModal(false);
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to apply for loan' });
    }
  }

  async function handleReview(loanId: string, status: 'active' | 'rejected') {
    try {
      await reviewLoan(loanId, status);
      setMsg({ type: 'success', text: `Loan ${status === 'active' ? 'approved and activated' : 'rejected'}!` });
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to review loan' });
    }
  }

  const filteredLoans = loans.filter((l) => {
    if (filter === 'all') return true;
    return l.status === filter;
  });

  return (
    <div>
      <div className="page-head">
        <div>
          <span className="eyebrow">
            <Icon name="loans" size="xs" />
            <span>Finance & Benefits</span>
          </span>
          <h1>Loans & Salary Advances</h1>
        </div>
        <button className="btn-primary" onClick={() => setApplyModal(true)}>
          <Icon name="plus" size="xs" />
          <span>Apply for Loan / Advance</span>
        </button>
      </div>

      {msg && <div className={`banner ${msg.type}`}>{msg.text}</div>}

      <div className="panel">
        <div className="panel-head">
          <strong>Loan & Advance Ledger</strong>
          <div className="toolbar">
            <select value={filter} onChange={(e) => setFilter(e.target.value as any)}>
              <option value="all">All Loans</option>
              <option value="pending">Pending Approval</option>
              <option value="active">Active (Repaying)</option>
              <option value="closed">Closed / Paid</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="empty">Loading loan records...</div>
        ) : (
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>Applied On</th>
                  <th>Employee</th>
                  <th>Type</th>
                  <th>Principal</th>
                  <th>Monthly EMI</th>
                  <th>Tenure</th>
                  <th>Remaining Balance</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLoans.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="empty">No loan applications found.</td>
                  </tr>
                ) : (
                  filteredLoans.map((l) => (
                    <tr key={l.id}>
                      <td className="mono">{l.created_at?.split('T')[0] || '—'}</td>
                      <td>
                        <strong>{l.first_name ? `${l.first_name} ${l.last_name || ''}` : session?.user.fullName}</strong>
                        <span className="muted mono" style={{ display: 'block', fontSize: 11 }}>{l.employee_code}</span>
                      </td>
                      <td>
                        <strong>{l.loan_type === 'advance' ? 'Salary Advance' : 'Personal Loan'}</strong>
                      </td>
                      <td className="mono money">₹{(l.principal_amount || 0).toLocaleString('en-IN')}</td>
                      <td className="mono money">₹{(l.emi_monthly_amount || 0).toLocaleString('en-IN')}/mo</td>
                      <td className="mono">{l.tenure_months} mos</td>
                      <td className="mono money" style={{ fontWeight: 700, color: l.remaining_balance > 0 ? 'var(--status-pending)' : 'var(--status-verified)' }}>
                        ₹{(l.remaining_balance || 0).toLocaleString('en-IN')}
                      </td>
                      <td><StatusBadge status={l.status} /></td>
                      <td>
                        {l.status === 'pending' && (can('loans.manage') || can('organization.manage')) ? (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              className="btn-success btn-sm"
                              onClick={() => void handleReview(l.id, 'active')}
                            >
                              <Icon name="checkCircle" size="xs" />
                              <span>Approve</span>
                            </button>
                            <button
                              className="btn-danger btn-sm"
                              onClick={() => void handleReview(l.id, 'rejected')}
                            >
                              <Icon name="xCircle" size="xs" />
                              <span>Reject</span>
                            </button>
                          </div>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Apply Loan Modal */}
      {applyModal && (
        <div className="modal-backdrop" onClick={() => setApplyModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <span className="eyebrow">Financial Support</span>
                <h3>Apply for Loan or Salary Advance</h3>
              </div>
              <button className="btn-icon" onClick={() => setApplyModal(false)} aria-label="Close">
                <Icon name="close" size="sm" />
              </button>
            </div>
            <form onSubmit={handleApply}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-group">
                  <label>Assistance Type</label>
                  <select value={loanType} onChange={(e) => setLoanType(e.target.value as any)}>
                    <option value="advance">Salary Advance (0% Interest · 1-3 Months Recovery)</option>
                    <option value="personal_loan">Personal Loan (8.5% p.a. · Up to 24 Months)</option>
                  </select>
                </div>
                <div className="form-grid-2">
                  <div className="form-group">
                    <label>Principal Amount (₹)</label>
                    <input
                      type="number"
                      min="5000"
                      step="1000"
                      required
                      value={principal}
                      onChange={(e) => setPrincipal(Number(e.target.value))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Tenure (Months)</label>
                    <input
                      type="number"
                      min="1"
                      max={loanType === 'advance' ? 3 : 24}
                      required
                      value={tenure}
                      onChange={(e) => setTenure(Number(e.target.value))}
                    />
                  </div>
                </div>

                {/* Calculated summary */}
                <div style={{ background: 'var(--bg-field)', padding: 14, borderRadius: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span>Interest Rate:</span>
                    <span className="mono">{estInterestRate}%</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span>Total Repayable:</span>
                    <span className="mono money">₹{totalRepayable.toLocaleString('en-IN')}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, paddingTop: 6, borderTop: '1px solid var(--border-subtle)' }}>
                    <span>Estimated Monthly Deduction (EMI):</span>
                    <span className="mono money" style={{ color: 'var(--brand-primary)' }}>₹{monthlyEmi.toLocaleString('en-IN')} / mo</span>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <button type="button" className="btn-quiet" onClick={() => setApplyModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Submit Application</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

